import { Refresh as RefreshIcon, Sync as SyncIcon } from "@mui/icons-material";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import {
  CheckinDeviceRecord,
  DeviceMqttPayload,
  mergeDeviceUpdate,
  useDeviceStatusMqtt,
} from "../../../hooks/useDeviceStatusMqtt";
import { getLocale } from "../../../i18n";
import { useTranslation } from "../../../i18n/useTranslation";
import { DeviceService } from "../../../services/device.service";
import Loading from "../../components/Loading";
import "./index.scss";

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString(getLocale() === "en" ? "en-US" : "vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    hour12: false,
  });
};

const Devices: React.FC = () => {
  const { t } = useTranslation();
  const [devices, setDevices] = useState<CheckinDeviceRecord[]>([]);
  const [mqttTopic, setMqttTopic] = useState<string | null>(null);
  const [offlineCount, setOfflineCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editingDevice, setEditingDevice] = useState<CheckinDeviceRecord | null>(null);
  const [editName, setEditName] = useState("");
  const [editLocation, setEditLocation] = useState("");
  const [editUsername, setEditUsername] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [credentialMsg, setCredentialMsg] = useState("");
  const [recoveryCodeOnce, setRecoveryCodeOnce] = useState("");
  const [saving, setSaving] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  const fetchDevices = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const result = await DeviceService.getDevices();
      const data = result.data.data;
      setDevices(data?.devices || []);
      setMqttTopic(data?.mqttTopic || null);
      setOfflineCount(data?.offlineCount || 0);
    } catch (error) {
      console.error(error);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleMqttUpdate = useCallback((payload: DeviceMqttPayload) => {
    setDevices((current) => {
      const next = mergeDeviceUpdate(current, payload);
      setOfflineCount(next.filter((item) => item.status === "offline").length);
      return next;
    });
  }, []);

  useDeviceStatusMqtt({
    mqttTopic,
    enabled: Boolean(mqttTopic),
    onUpdate: handleMqttUpdate,
  });

  const onlineCount = devices.filter((item) => item.status === "online").length;
  const missingCredentialCount = devices.filter((item) => !item.hasDeviceAccount).length;
  const missingRecoveryCount = devices.filter(
    (item) => item.hasDeviceAccount && !item.hasRecoveryCode
  ).length;

  const openEditDialog = (device: CheckinDeviceRecord) => {
    setEditingDevice(device);
    setEditName(device.name || "");
    setEditLocation(device.location || "");
    setEditUsername(device.accountUsername || "");
    setEditPassword("");
    setCredentialMsg("");
    setRecoveryCodeOnce("");
  };

  const saveDevice = async () => {
    if (!editingDevice) {
      return;
    }
    setSaving(true);
    try {
      await DeviceService.updateDevice(editingDevice.deviceId, {
        name: editName,
        location: editLocation,
      });
      setEditingDevice(null);
      fetchDevices(true);
    } finally {
      setSaving(false);
    }
  };

  const saveCredentials = async () => {
    if (!editingDevice) {
      return;
    }
    if (!editUsername.trim() || editPassword.length < 8) {
      setCredentialMsg(t("devices.passwordHint"));
      return;
    }
    setSaving(true);
    setCredentialMsg("");
    try {
      const res = await DeviceService.setCredentials(editingDevice.deviceId, {
        username: editUsername.trim(),
        password: editPassword,
      });
      setEditPassword("");
      const code = res?.data?.data?.recoveryCode;
      if (code) {
        setRecoveryCodeOnce(String(code));
        setCredentialMsg(`${t("devices.credentialsSaved")} ${t("devices.recoveryGenerated")} ${code}`);
      } else {
        setCredentialMsg(t("devices.credentialsSaved"));
      }
      fetchDevices(true);
      setEditingDevice((current) =>
        current
          ? {
              ...current,
              accountUsername: editUsername.trim(),
              hasDeviceAccount: true,
              hasRecoveryCode: Boolean(code) || current.hasRecoveryCode,
            }
          : current
      );
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "ERROR";
      setCredentialMsg(String(message));
    } finally {
      setSaving(false);
    }
  };

  const revokeCredentials = async () => {
    if (!editingDevice) {
      return;
    }
    setSaving(true);
    setCredentialMsg("");
    try {
      await DeviceService.revokeCredentials(editingDevice.deviceId);
      setEditUsername("");
      setEditPassword("");
      setCredentialMsg(t("devices.credentialsRevoked"));
      setEditingDevice((current) =>
        current ? { ...current, accountUsername: "", hasDeviceAccount: false } : current
      );
      fetchDevices(true);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "ERROR";
      setCredentialMsg(String(message));
    } finally {
      setSaving(false);
    }
  };

  const regenerateRecovery = async () => {
    if (!editingDevice) {
      return;
    }
    setSaving(true);
    setCredentialMsg("");
    try {
      const res = await DeviceService.regenerateRecoveryCode(editingDevice.deviceId);
      const code = res?.data?.data?.recoveryCode;
      if (code) {
        setRecoveryCodeOnce(String(code));
        setCredentialMsg(`${t("devices.recoveryGenerated")} ${code}`);
      }
      setEditingDevice((current) =>
        current ? { ...current, hasRecoveryCode: true } : current
      );
      fetchDevices(true);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "ERROR";
      setCredentialMsg(String(message));
    } finally {
      setSaving(false);
    }
  };

  const syncDevice = async (deviceId: string) => {
    try {
      setSyncingId(deviceId);
      await DeviceService.triggerDeviceSync(deviceId);
    } finally {
      setSyncingId(null);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className='devices-listing'>
      <div className='devices-listing__header'>
        <h2>{t("devices.title")}</h2>
        <Button startIcon={<RefreshIcon />} onClick={() => fetchDevices()}>
          {t("common.refresh")}
        </Button>
      </div>

      <div className='devices-listing__summary'>
        <div className='devices-listing__card'>
          {t("devices.total", { count: devices.length })}
        </div>
        <div className='devices-listing__card'>
          {t("devices.online", { count: onlineCount })}
        </div>
        <div className='devices-listing__card'>
          {t("devices.offline", { count: offlineCount })}
        </div>
      </div>

      {offlineCount > 0 && (
        <div className='devices-listing__card' style={{ marginBottom: "1.6rem", color: "#b91c1c" }}>
          {t("devices.offlineWarn", { count: offlineCount })}
        </div>
      )}

      {(missingCredentialCount > 0 || missingRecoveryCount > 0) && (
        <div className='devices-listing__card' style={{ marginBottom: "1.6rem", color: "#92400e" }}>
          {missingCredentialCount > 0
            ? `${t("dashboard.alertNoCredential", { count: missingCredentialCount })} `
            : ""}
          {missingRecoveryCount > 0
            ? t("dashboard.alertNoRecovery", { count: missingRecoveryCount })
            : ""}
        </div>
      )}

      <div className='devices-listing__table'>
        <div className='devices-listing__row devices-listing__row--head'>
          <div>{t("devices.name")}</div>
          <div>{t("devices.location")}</div>
          <div>{t("devices.account")}</div>
          <div>{t("devices.ip")}</div>
          <div>{t("devices.setup")}</div>
          <div>{t("devices.status")}</div>
          <div>{t("devices.lastSeen")}</div>
          <div>{t("devices.lastScan")}</div>
          <div>{t("common.actions")}</div>
        </div>
        {devices.map((device) => (
          <div className='devices-listing__row' key={device.deviceId}>
            <div>
              <strong>{device.name || device.deviceId}</strong>
              <div style={{ color: "#64748b", fontSize: "1.2rem" }}>
                v{device.appVersionCode || 0} · {device.deviceId}
              </div>
            </div>
            <div>{device.location || "-"}</div>
            <div style={{ color: device.hasDeviceAccount ? "#0f766e" : "#b45309" }}>
              {device.accountUsername || t("devices.noAccount")}
            </div>
            <div>{device.lastIp || "-"}</div>
            <div>
              {!device.hasDeviceAccount ? (
                <span style={{ color: "#b45309" }}>{t("devices.setupMissingAccount")}</span>
              ) : !device.hasRecoveryCode ? (
                <span style={{ color: "#b45309" }}>{t("devices.setupMissingRecovery")}</span>
              ) : (
                <span style={{ color: "#0f766e" }}>{t("devices.setupReady")}</span>
              )}
            </div>
            <div className='devices-listing__status'>
              <span
                className={`devices-listing__dot devices-listing__dot--${device.status}`}
              />
              {device.status === "online"
                ? t("common.online")
                : device.status === "offline"
                ? t("common.offline")
                : t("common.unknown")}
            </div>
            <div>{formatDateTime(device.lastSeenAt)}</div>
            <div>
              {device.lastScanEmployeeCode
                ? `${device.lastScanEmployeeCode} · ${formatDateTime(device.lastScanAt)}`
                : "-"}
            </div>
            <div className='devices-listing__actions'>
              <Button size='small' onClick={() => openEditDialog(device)}>
                {t("common.edit")}
              </Button>
              <Button
                size='small'
                startIcon={<SyncIcon />}
                disabled={syncingId === device.deviceId}
                onClick={() => syncDevice(device.deviceId)}
              >
                {t("devices.syncEmployees")}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={Boolean(editingDevice)} onClose={() => setEditingDevice(null)} fullWidth maxWidth='sm'>
        <DialogTitle>{t("devices.editTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin='normal'
            label={t("devices.name")}
            value={editName}
            onChange={(event) => setEditName(event.target.value)}
          />
          <TextField
            fullWidth
            margin='normal'
            label={t("devices.location")}
            value={editLocation}
            onChange={(event) => setEditLocation(event.target.value)}
          />
          <div style={{ marginTop: "1.2rem", marginBottom: "0.4rem", fontWeight: 600 }}>
            {t("devices.account")}
          </div>
          <TextField
            fullWidth
            margin='normal'
            label={t("devices.username")}
            value={editUsername}
            autoComplete='off'
            onChange={(event) => setEditUsername(event.target.value)}
          />
          <TextField
            fullWidth
            margin='normal'
            type='password'
            label={t("devices.password")}
            value={editPassword}
            autoComplete='new-password'
            helperText={t("devices.passwordHint")}
            onChange={(event) => setEditPassword(event.target.value)}
          />
          {credentialMsg ? (
            <div style={{ marginTop: "0.8rem", color: "#0f766e", fontSize: "1.3rem" }}>
              {credentialMsg}
            </div>
          ) : null}
          <div style={{ marginTop: "1.6rem", marginBottom: "0.4rem", fontWeight: 600 }}>
            {t("devices.recoveryCode")}
          </div>
          <div style={{ color: "#64748b", fontSize: "1.2rem", marginBottom: "0.8rem" }}>
            {t("devices.recoveryHint")}
          </div>
          {recoveryCodeOnce ? (
            <div
              style={{
                fontFamily: "monospace",
                fontSize: "1.8rem",
                letterSpacing: "0.12em",
                padding: "0.8rem 1rem",
                background: "#ecfdf5",
                borderRadius: 8,
                marginBottom: "0.8rem",
              }}
            >
              {recoveryCodeOnce}
            </div>
          ) : (
            <div style={{ marginBottom: "0.8rem", color: "#b45309" }}>
              {editingDevice?.hasRecoveryCode
                ? t("devices.recoveryHint")
                : t("devices.recoveryMissing")}
            </div>
          )}
          <Button size='small' disabled={saving} onClick={regenerateRecovery}>
            {t("devices.generateRecovery")}
          </Button>
        </DialogContent>
        <DialogActions style={{ flexWrap: "wrap", gap: "0.5rem" }}>
          <Button onClick={() => setEditingDevice(null)}>{t("common.cancel")}</Button>
          {editingDevice?.hasDeviceAccount || editingDevice?.accountUsername ? (
            <Button color='warning' disabled={saving} onClick={revokeCredentials}>
              {t("devices.revokeCredentials")}
            </Button>
          ) : null}
          <Button variant='outlined' disabled={saving} onClick={saveCredentials}>
            {t("devices.saveCredentials")}
          </Button>
          <Button variant='contained' disabled={saving} onClick={saveDevice}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Devices;
